<template>
    <div class="files-list">
        <FileRow
            v-for="(file, idx) in files"
            :key="file.localId"
            :file="file"
            :is-last="idx === files.length - 1"
        ></FileRow>
    </div>
</template>

<script>
import { assertIsNumber } from '../../../lib/test-and-assert/assert-base';
import { SET_PASSWORD_ERROR_ALREADY_SETTING, SET_PASSWORD_ERROR_WRONG_PASSWORD } from '../core/consts';
import { globalLogger } from '../../../qaamgo/helper/global-logger';
import { formatting } from '../../../qaamgo/helper/formatting';
import { INPUT_EVENT_UPLOAD_PROGRESS, UPLOADER_EVENT } from '../core/event';
import { pageNotification } from '../../../qaamgo/helper/notify';
import FileRow from './views/FileRow';
import { eventBus } from '../../../lib/event-bus/event-bus';
import { testIsInputEvent } from '../core/test';

class File {
    /**
     * @param {Number} localId
     */
    constructor(localId) {
        this.localId = localId;
        this.status = 'UNDEFINED';
        this.type = 'UNDEFINED';
        this.size = null;
        this.size_readable = null;
        this.name = null;
        this.progressPercent = 0; // between 0 and 100
        this.progressText = '';
        /** @type {Number[]} */
        this.progressBitrates = [];
        this.progressAverageBitrate = 0;
        this.metadata = {};
        this.lastEventData = null;
    }

    /**
     * @param {Input} input
     */
    setDataFromInput(input) {
        this.status = input.getStatus();
        this.type = input.getType();
        this.name = input.getName();
        this.size = input.getSize();
        this.size_readable = formatting.fileSize(input.getSize());

        if (input._metadata) {
            this.metadata = input._metadata;
        }
    }

    /**
     * @param {Number} currentBitrate
     */
    updateAverageBitrate(currentBitrate) {
        assertIsNumber(currentBitrate);

        this.progressBitrates.push(currentBitrate);

        // only take the newest bitrates
        this.progressBitrates = this.progressBitrates.slice(-20);

        let total = 0;

        this.progressBitrates.forEach((bitrate) => {
            total += bitrate;
        });

        this.progressAverageBitrate = total / this.progressBitrates.length;
    }
}

export default {
    name: 'TheUploadList',
    components: { FileRow },
    created() {
        globalLogger.log('uploader file list - created TheUploadList');
        eventBus.listen(UPLOADER_EVENT, (uploaderEvent) => {
            if (!testIsInputEvent(uploaderEvent.event)) {
                return;
            }

            this.onUploaderEvent(uploaderEvent.event, uploaderEvent.localId, uploaderEvent.data);
        });
    },
    data() {
        return {
            /**
             * @var {File[]}
             */
            files: [],
        };
    },
    methods: {
        onUploaderEvent(event, localId, data) {
            //TODO react to 402 failed inputs

            try {
                if (localId > this.files.length) {
                    globalLogger.log('upload list fail id error');

                    return;
                }

                if (localId === this.files.length) {
                    const file = new File(localId);

                    this.files.push(file);
                }

                /** @type {Input} */
                const input = global.qgUploader.getInput(localId);

                this.files[localId].setDataFromInput(input);
                this.files[localId].lastEventData = data;

                if (event === INPUT_EVENT_UPLOAD_PROGRESS) {
                    const progressData = this.handleUploadProgress(this.files[localId], data);

                    this.files[localId].progressPercent = progressData.progressPercent;

                    this.files[localId].progressText = progressData.progressText;
                }
            } catch (e) {
                globalLogger.log('upload list fail exception', e);
            }
        },
        /**
         * @param {File} file
         * @param {ProgressData} data
         *
         * @returns {{progressPercent: number, progressText: string}}
         */
        handleUploadProgress(file, data) {
            // DEAL WITH PERCENTAGE
            const rawPercentage = (data.bytesLoaded / data.bytesTotal) * 100;
            const progressPercentage = Math.floor(rawPercentage);

            // DEAL WITH BITRATE
            file.updateAverageBitrate(data.bitrate);

            // DEAL WITH TIME LEFT DATA
            const bytesRemaining = data.bytesTotal - data.bytesLoaded;
            const secondsRemaining = (bytesRemaining * 8) / file.progressAverageBitrate + 1;
            const timeLeft = formatting.time(secondsRemaining);

            return {
                progressPercent: progressPercentage,
                progressText: timeLeft,
            };
        },
        cancelInput(localId) {
            try {
                window.qgUploader.cancelFileUpload(localId);
            } catch (e) {
                globalLogger.log('upload list fail exception', e);
            }
        },
        deleteInput(localId) {
            try {
                window.qgUploader.deleteInput(localId);
            } catch (e) {
                globalLogger.log('upload list fail exception', e);
            }
        },
        failInput(localId) {
            try {
                window.qgUploader.failInput(localId);
            } catch (e) {
                globalLogger.log('upload list fail exception', e);
            }
        },
        setPassword(localId, password) {
            try {
                window.qgUploader
                    .setPassword(localId, password)
                    .done(function () {
                        // nothing to do :)
                        // => file list will be notified of state changes via events
                    })
                    .fail(function (error) {
                        if (error === SET_PASSWORD_ERROR_WRONG_PASSWORD) {
                            pageNotification.error('Wrong password, please enter the correct one!');

                            return;
                        }

                        if (error === SET_PASSWORD_ERROR_ALREADY_SETTING) {
                            // do nothing
                            // => file list will be notified of state changes via events
                            return;
                        }

                        pageNotification.showGenericErrorMessage();
                        // pageNotification.error('Failed to set password. Please try again!');
                    });
            } catch (e) {
                globalLogger.log('upload list fail exception', e);
            }
        },
    },
};
</script>
